import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FormArray, FormControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { NotificationService } from 'app/_services/notification.service';
import { Globals } from 'app/_models/globals';
import { IntegrationTypeEnum } from 'app/_models/integrationType';
import { HoldingIntegration } from '../../_models/holdingIntegration';
import { SupportIntegrationManagementService } from '../../integration-management.service';
import { HoldingIntegrationOptions } from '../../_models/holdingIntegrationOptions';
import { ApiResponseModel } from 'app/_models/apiResponseModel';
import { SaveHoldingIntegration, holdingIntegrationForEditFactory } from '../../_models/saveHoldingIntegration';
import { NgScrollbar } from 'ngx-scrollbar';

@Component({
    selector: 'app-integration-manage', templateUrl: './integration-manage.component.html', styleUrls: ['./integration-manage.component.scss'],
    standalone: false
})
export class SupportManageIntegrationComponent {

    @Input() integration?: HoldingIntegration = null;
    @Output() saveIntegration = new EventEmitter<SaveHoldingIntegration>();
    @Output() goBackToList = new EventEmitter<null>();
    @Output() onIntegrationUpdate: EventEmitter<HoldingIntegration> = new EventEmitter<HoldingIntegration>();
    @ViewChild(NgScrollbar) scrollbarRef!: NgScrollbar;

    holdingIntegrationOptions: HoldingIntegrationOptions[];
    integrationManageForm: UntypedFormGroup;
    isLoading: boolean = false;
    isEditing: boolean = false;
    isSenderEmailSectionDisabled = false;
    isSenderEmailButtonDisabled = false;
    isResponseEmailButtonDisabled = false;
    showPassword = false;
    maxEmailsInput: number = 5;
    validationMessages: any;

    delimiterOptions = [
        { value: ';', label: 'support.manage-integrations.manage.delimiters.semicolon' },
        { value: ',', label: 'support.manage-integrations.manage.delimiters.colon' },
        { value: '|', label: 'support.manage-integrations.manage.delimiters.pipe' },
    ];

    integrationTypeOptions = [
        { value: IntegrationTypeEnum.Email, label: 'support.manage-integrations.manage.integration-types.email' },
        { value: IntegrationTypeEnum.FTP, label: 'support.manage-integrations.manage.integration-types.ftp' },
    ];

    constructor(private formBuilder: UntypedFormBuilder, private integrationManageService: SupportIntegrationManagementService, private notificationService: NotificationService, private globals: Globals) {
        this.integrationManageForm = this.formBuilder.group({
            integrationId: [""],
            holdingId: ["", [Validators.required]],
            integrationType: ["", Validators.required],
            isAutomatic: ["", Validators.required],
            partNumberIndex: ["", [Validators.required]],
            partNameIndex: ["", [Validators.required]],
            brandIndex: [""],
            brandIdIndex: [""],
            discountIndex: [""],
            quantityIndex: ["", [Validators.required]],
            partFactoryNumberIndex: [""],
            partTypeIdIndex: ["", [Validators.required]],
            priceIndex: ["", [Validators.required]],
            hasHeader: ["", Validators.required],
            delimiter: ["", Validators.required],
            senderEmails: this.formBuilder.array([]),
            responseEmails: this.formBuilder.array([]),
            usernameFtp: [{ value: "", disabled: true }],
            passwordFtp: [{ value: "", disabled: true }],
        });

        this.addSenderEmail();
        this.addResponseEmail();

        this.validationMessages = {
            holdingId: {
                required: this.globals.translate('support.manage-integrations.validation-messages.holding-id')
            },
            integrationType: {
                required: this.globals.translate('support.manage-integrations.validation-messages.integration-type')
            },
            isAutomatic: {
                required: this.globals.translate('support.manage-integrations.validation-messages.is-automatic')
            },
            partNumberIndex: {
                required: this.globals.translate('support.manage-integrations.validation-messages.part-number-index')
            },
            partNameIndex: {
                required: this.globals.translate('support.manage-integrations.validation-messages.part-name-index')
            },
            quantityIndex: {
                required: this.globals.translate('support.manage-integrations.validation-messages.quantity-index')
            },
            priceIndex: {
                required: this.globals.translate('support.manage-integrations.validation-messages.price-index')
            },
            hasHeader: {
                required: this.globals.translate('support.manage-integrations.validation-messages.has-header')
            },
            delimiter: {
                required: this.globals.translate('support.manage-integrations.validation-messages.delimiter')
            },
        };
    }

    ngOnInit() {
        this.isEditing = this.integration != null;
        this.handleIntegrationFormSet();
        this.loadHoldingIntegrationOptions(this.isEditing, 0);
    }

    addSenderEmail(scrollToEnd: boolean = false): void {
        let control = this.formBuilder.control('', [Validators.required, Validators.email]);
        control.markAsTouched();

        this.senderEmails.push(control);
        this.integrationManageForm.markAsTouched();

        if (scrollToEnd) {
            setTimeout(() => {
                this.scrollbarRef?.scrollTo({ bottom: 0, duration: 300 });
            });
        }

        this.toggleSenderEmail();
    }
    
    addResponseEmail(scrollToEnd: boolean = false): void {
        let control = this.formBuilder.control('', [Validators.required, Validators.email]);
        control.markAsTouched();

        this.responseEmails.push(control);
        this.integrationManageForm.markAsTouched();

        if (scrollToEnd) {
            setTimeout(() => {
                this.scrollbarRef?.scrollTo({ bottom: 0, duration: 300 });
            });
        }

        this.toggleResponseEmail();
    }

    close() {
        this.goBackToList.emit();
    }

    handleIntegrationFormSet() {
        if (this.integration) {
            this.senderEmails.clear();
            this.responseEmails.clear();

            let senderEmails = this.integration.senderEmails.length > 0 ? this.integration.senderEmails : [''];
            senderEmails.forEach(email => this.senderEmails.push(this.formBuilder.control(email, [Validators.required, Validators.email])));           

            this.integration.responseEmails.forEach(email => this.responseEmails.push(new FormControl(email, [Validators.required, Validators.email])));

            this.integrationManageForm.setValue({
                integrationId: this.integration.integrationId,
                holdingId: this.integration.holdingId,
                integrationType: this.integration.integrationType,
                isAutomatic: this.integration.isAutomatic,
                hasHeader: this.integration.hasHeader,
                delimiter: this.integration.delimiter,
                partNameIndex: this.integration.partNameIndex,
                brandIndex: this.integration.brandIndex,
                brandIdIndex: this.integration.brandIdIndex,
                discountIndex: this.integration.discountIndex,
                quantityIndex: this.integration.quantityIndex,
                partFactoryNumberIndex: this.integration.partFactoryNumberIndex,
                partNumberIndex: this.integration.partNumberIndex,
                partTypeIdIndex: this.integration.partTypeIdIndex,
                priceIndex: this.integration.priceIndex,
                senderEmails: senderEmails,
                responseEmails: this.integration.responseEmails,
                usernameFtp: this.integration.usernameFtp,
                passwordFtp: this.integration.passwordFtp
            });
            
            this.isSenderEmailSectionDisabled = this.integration.integrationType == IntegrationTypeEnum.FTP;
            this.integrationManageForm.get('holdingId')?.disable();
            this.toggleResponseEmail();
            this.toggleSenderEmail();
        }
    }

    isFormValid(): boolean {
        Object.keys(this.integrationManageForm.controls).forEach(field => {
            const control = this.integrationManageForm.get(field);
            control?.markAsTouched({ onlySelf: true });
            control?.markAsDirty({ onlySelf: true });
        });

        if (this.integrationManageForm.valid)
            return true;

        Object.keys(this.validationMessages).forEach(field => {
            const control = this.integrationManageForm.get(field);
            const errorMessages = this.validationMessages[field];

            if (control?.invalid) {
                Object.keys(errorMessages).forEach(errorKey => {
                    if (control.errors?.[errorKey]) {
                        this.notificationService.showErrorToastr(errorMessages[errorKey]);
                    }
                });
            }
        });

        let hasInvalidResponseEmail = this.responseEmails.controls.some(control => control.invalid);

        if (hasInvalidResponseEmail) {
            this.responseEmails.controls.forEach(control => {
                if (control.invalid) {
                    control.markAsDirty();
                    control.markAsTouched();
                }
            });

            this.responseEmails.setErrors({ invalid: true });
            this.responseEmails.markAsDirty();
            this.notificationService.showErrorToastr('support.manage-integrations.validation-messages.invalid-response-email', { translateMessage: true });
            return false;
        }

        let integrationTypeControl = this.integrationManageForm.get('integrationType');
        if (integrationTypeControl.value == IntegrationTypeEnum.Email) {
            let hasInvalidSenderEmail = this.senderEmails.controls.some(control => control.invalid);

            if (hasInvalidSenderEmail) {
                this.senderEmails.controls.forEach(control => {
                    if (control.invalid) {
                        control.markAsDirty();
                        control.markAsTouched();
                    }
                });

                this.senderEmails.setErrors({ invalid: true });
                this.senderEmails.markAsDirty();
                this.notificationService.showErrorToastr('support.manage-integrations.validation-messages.invalid-sender-email', { translateMessage: true });
                return false;
            }
        }

        return true;
    }
    
    loadHoldingIntegrationOptions(isCreating: boolean, nAttempts: number) {
        this.integrationManageService.getHoldings(isCreating).subscribe({
            next: (response: ApiResponseModel<HoldingIntegrationOptions[]>) => {
                if (response != null && response.success) {
                    this.holdingIntegrationOptions = response.result;
                }
            },
            error: error => {
                nAttempts = nAttempts || 1;
                console.log(error, nAttempts);

                if (nAttempts >= 5) {
                    this.notificationService.showErrorToastr(this.globals.translate('support.manutencao.cadastro-cnpj.mensagens.listar.erro.corpo'));
                    return;
                }

                this.loadHoldingIntegrationOptions(isCreating, ++nAttempts);
            }
        });
    }

    updateIntegration(integration: HoldingIntegration) {
        this.integration = integration;

        this.handleIntegrationFormSet()
        this.onIntegrationUpdate.emit(this.integration);
    }

    save() {
        if (!this.integrationManageForm.touched) {
            this.close();
            return;
        }

        if (!this.isFormValid())
            return;

        this.isLoading = true;

        const formValue = this.integrationManageForm.getRawValue();
        const integration = holdingIntegrationForEditFactory(formValue.integrationId,
                                                             formValue.holdingId, 
                                                             formValue.integrationType,
                                                             formValue.isAutomatic,
                                                             formValue.hasHeader,
                                                             formValue.delimiter,
                                                             formValue.partNameIndex,
                                                             formValue.brandIndex,
                                                             formValue.brandIdIndex,
                                                             formValue.discountIndex,
                                                             formValue.quantityIndex,
                                                             formValue.partFactoryNumberIndex,
                                                             formValue.partNumberIndex,
                                                             formValue.partTypeIdIndex,
                                                             formValue.priceIndex,
                                                             formValue.senderEmails,
                                                             formValue.responseEmails);

        try {
            this.saveIntegration.emit(integration);
        } finally {
            this.isLoading = false;
        }
    }

    removeResponseEmail(index: number): void {
        this.responseEmails.removeAt(index);
        this.integrationManageForm.markAsTouched();
        this.isResponseEmailButtonDisabled = false;
    }

    removeSenderEmail(index: number): void {
        this.senderEmails.removeAt(index);
        this.integrationManageForm.markAsTouched();
        this.isSenderEmailButtonDisabled = false;
    }

    onIntegrationTypeChange() {
        let integrationType = this.integrationManageForm.get('integrationType')?.value;
        this.isSenderEmailSectionDisabled = integrationType == IntegrationTypeEnum.FTP;

        if (this.isSenderEmailSectionDisabled) {
            this.senderEmails.clear();
            this.senderEmails.push(this.formBuilder.control(''));
        }
    }

    toggleSenderEmail() {
        if (this.senderEmails.length == this.maxEmailsInput) {
            this.isSenderEmailButtonDisabled = true;
            return;
        }
    }

    toggleResponseEmail() {
        if (this.responseEmails.length == this.maxEmailsInput) {
            this.isResponseEmailButtonDisabled = true;
            return;
        }
    }

    toggleShowPassword() {
        this.showPassword = !this.showPassword;
    }

    get senderEmails(): FormArray<FormControl> {
        return this.integrationManageForm.get('senderEmails') as FormArray<FormControl>;
    }

    get responseEmails(): FormArray<FormControl> {
        return this.integrationManageForm.get('responseEmails') as FormArray<FormControl>;
    }
}
